/**
 * grip-configurator.js
 * ---------------------------------------------------------------
 * Drives the "Grip Change" configurator page (ship-in only).
 *
 * COLOR OPTION
 * If the service product has a third variant option (in addition to
 * Grip Model and Tape Layers), it's treated as "Color" and a color
 * dropdown appears automatically. Products without a third option
 * simply don't show a color field — nothing to configure.
 *
 * IMAGES
 * Grip photos are pulled from whatever image is assigned to each
 * variant in Shopify admin (Grip Change product -> variant -> image),
 * based on grip model + color (ignoring tape layers). Tape-layer
 * photos are completely separate: 3 fixed images (one per layer
 * count) set once in the section's theme editor settings, shown
 * regardless of which grip model is selected — no need to shoot a
 * tape photo for every single grip model.
 *
 * CURRENCY
 * All prices shown come pre-formatted from Liquid (see the "prices"
 * lookup embedded in grip-configurator-content.liquid), already
 * converted and formatted server-side via Shopify's `money` filter.
 * This file never does currency math — it only looks values up by
 * variant id and quantity, so switching currency via your store's
 * picker and reloading always shows the correct amount.
 *
 * MINIMUM QUANTITY
 * Standard grips require a minimum of 5 clubs; putter grips have no
 * minimum. Switching grip type automatically adjusts the quantity
 * stepper's minimum and resets the quantity when the minimum itself
 * changes (so switching standard -> putter drops 5 down to 1, but
 * switching back and forth without the minimum actually changing
 * won't wipe out a quantity you've manually typed in).
 */

function initAllGripConfigurators(scope) {
  scope.querySelectorAll('[data-grip-config]').forEach(function (root) {
    try {
      initGripConfigurator(root);
    } catch (e) {
      if (window.console) console.error('Grip configurator: initialization failed', e);
    }
  });
}

document.addEventListener('DOMContentLoaded', function () {
  initAllGripConfigurators(document);
});

// Shopify's Theme Editor swaps in freshly re-rendered section HTML
// whenever a setting changes (e.g. uploading a tape-layer image) —
// without a full page reload. That fresh HTML has no JS attached to
// it yet, so without this, dropdowns/images stay empty after any
// settings change in the editor. This re-runs initialization on the
// section that was actually updated.
document.addEventListener('shopify:section:load', function (event) {
  initAllGripConfigurators(event.target);
});

function initGripConfigurator(root) {
  var I18N = (function () {
    var d = (root && root.dataset) || {};
    function get(key, fallback) {
      return d[key] == null || d[key] === '' ? fallback : d[key];
    }
    return {
      minClubs: get('i18nMinClubs', 'Requires at least __N__ clubs.'),
      noMinimum: get('i18nNoMinimum', 'No minimum club count for putter grips.'),
      combinationMissing: get('i18nCombinationMissing', 'Could not find that combination. Please try again.'),
      minClubsError: get('i18nMinClubsError', 'Requires at least __N__ clubs. Please increase your quantity.'),
      adding: get('i18nAdding', 'Adding...'),
      added: get('i18nAdded', 'Added to your cart.'),
      addFailed: get('i18nAddFailed', 'Something went wrong. Please try again.'),
      propGripType: get('i18nPropGripType', 'Grip Type'),
      propClubs: get('i18nPropClubs', 'Number of Clubs'),
      propColor: get('i18nPropColor', 'Color'),
      propShippingFrom: get('i18nPropShippingFrom', 'Shipping From'),
      valuePutter: get('i18nValuePutter', 'Putter grip'),
      valueStandard: get('i18nValueStandard', 'Standard grip')
    };
  })();

  function withCount(str, n) {
    return String(str).replace('__N__', n);
  }

  var dataEl = root.querySelector('[data-grip-config-data]');
  var data = {
    standard: { variants: [], optionNames: [], prices: {} },
    putter: { variants: [], optionNames: [], prices: {} }
  };
  try {
    var parsed = JSON.parse(dataEl.textContent);
    if (parsed && parsed.standard && parsed.putter) {
      data = parsed;
    }
  } catch (e) {
    if (window.console) console.error('Grip configurator: failed to parse product data', e);
  }

  var fallbackImages = {
    standard: root.dataset.fallbackStandard || '',
    putter: root.dataset.fallbackPutter || ''
  };

  var tapeLayerImages = {
    '1': root.getAttribute('data-tape-image-1') || '',
    '2': root.getAttribute('data-tape-image-2') || '',
    '3': root.getAttribute('data-tape-image-3') || ''
  };

  var minQty = {
    standard: parseInt(root.dataset.minQtyStandard, 10) || 5,
    putter: parseInt(root.dataset.minQtyPutter, 10) || 1
  };
  var maxQty = parseInt(root.dataset.maxQty, 10) || 14;

  var locationSelect = root.querySelector('[data-grip-location]');
  var addressEl = root.querySelector('[data-grip-address]');
  var typeRadios = root.querySelectorAll('[data-grip-type]');
  var modelSelect = root.querySelector('[data-grip-model]');
  var colorField = root.querySelector('[data-grip-color-field]');
  var colorSelect = root.querySelector('[data-grip-color]');
  var layersSelect = root.querySelector('[data-grip-layers]');
  var previewImg = root.querySelector('[data-grip-preview-img]');
  var previewEmpty = root.querySelector('[data-grip-preview-empty]');
  var tapePreviewImg = root.querySelector('[data-grip-tape-preview-img]');
  var tapePreviewEmpty = root.querySelector('[data-grip-tape-preview-empty]');
  var unitPriceEl = root.querySelector('[data-grip-unit-price]');
  var qtyInput = root.querySelector('[data-grip-qty]');
  var qtyDecrease = root.querySelector('[data-grip-qty-decrease]');
  var qtyIncrease = root.querySelector('[data-grip-qty-increase]');
  var qtyNoteEl = root.querySelector('[data-grip-qty-note]');
  var priceEl = root.querySelector('[data-grip-price]');
  var addBtn = root.querySelector('[data-grip-add]');
  var statusEl = root.querySelector('[data-grip-status]');

  if (!modelSelect || !layersSelect || !addBtn) return;

  function currentType() {
    var checked = root.querySelector('[data-grip-type]:checked');
    return checked ? checked.value : 'standard';
  }

  function typeData() {
    return data[currentType()] || { variants: [], optionNames: [], prices: {} };
  }

  function hasColor() {
    return (typeData().optionNames || []).length >= 3;
  }

  function uniqueValues(variants, key) {
    var seen = [];
    variants.forEach(function (v) {
      if (v[key] && seen.indexOf(v[key]) === -1) seen.push(v[key]);
    });
    return seen;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function populateSelects() {
    var variants = typeData().variants || [];
    var models = uniqueValues(variants, 'option1');

    modelSelect.innerHTML = models
      .map(function (m) {
        return '<option value="' + escapeHtml(m) + '">' + escapeHtml(m) + '</option>';
      })
      .join('');

    colorField.hidden = !hasColor();
    populateColors();
    populateLayers();
    updateQtyConstraints();
    updateAll();
  }

  function populateColors() {
    if (!hasColor()) {
      colorSelect.innerHTML = '';
      return;
    }
    var variants = typeData().variants || [];
    var model = modelSelect.value;
    var colors = uniqueValues(
      variants.filter(function (v) {
        return v.option1 === model;
      }),
      'option3'
    );
    colorSelect.innerHTML = colors
      .map(function (c) {
        return '<option value="' + escapeHtml(c) + '">' + escapeHtml(c) + '</option>';
      })
      .join('');
  }

  function populateLayers() {
    var variants = typeData().variants || [];
    var model = modelSelect.value;
    var color = hasColor() ? colorSelect.value : null;

    var candidates = variants.filter(function (v) {
      if (v.option1 !== model) return false;
      if (hasColor() && color && v.option3 !== color) return false;
      return true;
    });

    var layers = uniqueValues(candidates, 'option2').sort(function (a, b) {
      return parseInt(a, 10) - parseInt(b, 10);
    });

    layersSelect.innerHTML = layers
      .map(function (l) {
        var digits = (String(l).match(/\d+/) || [])[0] || l;
        var label = digits === '1' ? '1 layer' : digits + ' layers';
        return '<option value="' + escapeHtml(l) + '">' + escapeHtml(label) + '</option>';
      })
      .join('');
  }

  function findVariant() {
    var variants = typeData().variants || [];
    var model = modelSelect.value;
    var color = hasColor() ? colorSelect.value : null;
    var layers = layersSelect.value;

    return (
      variants.find(function (v) {
        if (v.option1 !== model) return false;
        if (v.option2 !== layers) return false;
        if (hasColor() && v.option3 !== color) return false;
        return true;
      }) || null
    );
  }

  function priceEntryFor(variant) {
    if (!variant) return null;
    var prices = typeData().prices || {};
    return prices[String(variant.id)] || null;
  }

  function setPreviewImage(imgEl, emptyEl, src, alt) {
    if (!src) {
      imgEl.hidden = true;
      imgEl.removeAttribute('src');
      emptyEl.hidden = false;
      return;
    }
    imgEl.onload = function () {
      imgEl.hidden = false;
      emptyEl.hidden = true;
    };
    imgEl.onerror = function () {
      imgEl.hidden = true;
      emptyEl.hidden = false;
    };
    imgEl.alt = alt || '';
    imgEl.src = src;
  }

  function updatePreviewImage() {
    var variants = typeData().variants || [];
    var model = modelSelect.value;
    var color = hasColor() ? colorSelect.value : null;

    // Grip preview: best model+color match, ignoring tape layers
    // (the photo of the grip itself usually doesn't change per layer).
    var gripMatch =
      variants.find(function (v) {
        if (v.option1 !== model) return false;
        if (hasColor() && color && v.option3 !== color) return false;
        return v.featured_image && v.featured_image.src;
      }) || null;

    var gripSrc = (gripMatch && gripMatch.featured_image && gripMatch.featured_image.src) || fallbackImages[currentType()];
    setPreviewImage(previewImg, previewEmpty, gripSrc, model + (color ? ' - ' + color : ''));

    // Tape preview: a fixed photo per layer count, independent of grip
    // model/color — one photo per layer count total, not one per
    // model+layer combination. Extract just the leading digit from the
    // layer value in case it includes extra text like "1 (Minimum)".
    var layersValue = layersSelect.value;
    var layersDigits = (String(layersValue).match(/\d+/) || [])[0] || '';
    var tapeSrc = tapeLayerImages[layersDigits];
    var layersLabel = layersValue ? layersValue : '';
    setPreviewImage(tapePreviewImg, tapePreviewEmpty, tapeSrc, layersLabel);
  }

  function updateUnitPrice() {
    var variant = findVariant();
    var entry = priceEntryFor(variant);
    unitPriceEl.textContent = entry ? entry.unit + ' per grip' : '';
  }

  function updatePrice() {
    var variant = findVariant();
    var entry = priceEntryFor(variant);
    var qty = clampQty(parseInt(qtyInput.value, 10) || minQtyForType());

    if (!entry) {
      priceEl.textContent = '';
      addBtn.disabled = true;
      return;
    }

    var total = entry.totals[qty - 1] || entry.totals[entry.totals.length - 1];
    priceEl.textContent = 'Total: ' + total;
    addBtn.disabled = false;
  }

  function minQtyForType() {
    return currentType() === 'putter' ? minQty.putter : minQty.standard;
  }

  function clampQty(value) {
    var min = minQtyForType();
    return Math.min(maxQty, Math.max(min, value || min));
  }

  var lastAppliedMin = null;

  function updateQtyConstraints() {
    var min = minQtyForType();
    qtyInput.min = min;
    // Only snap the quantity to the new minimum when the minimum itself
    // has actually changed (e.g. standard -> putter drops the minimum
    // from 5 to 1). This avoids wiping out a quantity the customer
    // already typed in when nothing about the minimum actually changed.
    if (lastAppliedMin === null || min !== lastAppliedMin) {
      qtyInput.value = min;
      lastAppliedMin = min;
    } else {
      var current = parseInt(qtyInput.value, 10) || min;
      if (current < min) qtyInput.value = min;
    }
    qtyNoteEl.textContent =
      min > minQty.putter ? withCount(I18N.minClubs, min) : I18N.noMinimum;
  }

  function updateAll() {
    updatePreviewImage();
    updateUnitPrice();
    updatePrice();
  }

  function updateAddress() {
    if (!locationSelect || !addressEl) return;
    var selected = locationSelect.options[locationSelect.selectedIndex];
    addressEl.textContent = selected ? selected.getAttribute('data-address') || '' : '';
  }

  function setStatus(text, state) {
    statusEl.textContent = text;
    if (state) {
      statusEl.setAttribute('data-state', state);
    } else {
      statusEl.removeAttribute('data-state');
    }
  }

  typeRadios.forEach(function (radio) {
    radio.addEventListener('change', populateSelects);
  });
  modelSelect.addEventListener('change', function () {
    populateColors();
    populateLayers();
    updateAll();
  });
  colorSelect.addEventListener('change', function () {
    populateLayers();
    updateAll();
  });
  layersSelect.addEventListener('change', updateAll);
  qtyInput.addEventListener('input', function () {
    updatePrice();
  });
  qtyInput.addEventListener('change', function () {
    qtyInput.value = clampQty(parseInt(qtyInput.value, 10));
    updatePrice();
  });
  if (locationSelect) locationSelect.addEventListener('change', updateAddress);

  function stepQty(delta) {
    var current = parseInt(qtyInput.value, 10) || minQtyForType();
    qtyInput.value = clampQty(current + delta);
    updatePrice();
  }

  if (qtyDecrease) qtyDecrease.addEventListener('click', function () { stepQty(-1); });
  if (qtyIncrease) qtyIncrease.addEventListener('click', function () { stepQty(1); });

  addBtn.addEventListener('click', function () {
    var variant = findVariant();
    var entry = priceEntryFor(variant);
    var type = currentType();
    var min = minQtyForType();
    var qty = clampQty(parseInt(qtyInput.value, 10) || min);

    if (!variant || !entry) {
      setStatus(I18N.combinationMissing, 'error');
      return;
    }

    if (qty < min) {
      setStatus(withCount(I18N.minClubsError, min), 'error');
      return;
    }

    addBtn.disabled = true;
    setStatus(I18N.adding, '');

    var properties = {
      [I18N.propGripType]: type === 'putter' ? I18N.valuePutter : I18N.valueStandard,
      [I18N.propClubs]: String(qty)
    };
    if (hasColor()) {
      properties[I18N.propColor] = colorSelect.value;
    }
    if (locationSelect) {
      var selectedOption = locationSelect.options[locationSelect.selectedIndex];
      properties[I18N.propShippingFrom] = selectedOption ? selectedOption.textContent : '';
    }

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variant.id,
            quantity: qty,
            properties: properties
          }
        ]
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not add to cart');
        return res.json();
      })
      .then(function () {
        setStatus(I18N.added, 'success');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () {
        setStatus(I18N.addFailed, 'error');
      })
      .finally(function () {
        addBtn.disabled = false;
      });
  });

  // Pre-fill grip type from the product-page link, e.g. ?type=putter
  var params = new URLSearchParams(window.location.search);
  var presetType = params.get('type');
  if (presetType === 'putter' || presetType === 'standard') {
    var match = root.querySelector('[data-grip-type][value="' + presetType + '"]');
    if (match) match.checked = true;
  }

  updateAddress();

  try {
    populateSelects();
  } catch (e) {
    if (window.console) console.error('Grip configurator: failed to populate selects', e);
  }
}
